/**
 * Individual action item in an action list with drag handle, edit/delete
 * buttons, and action type icon. Used in the action editor workflow.
 */
import { GripVertical, Pencil, Trash2 } from "lucide-react";


import type { ModuleActionInfo } from "./ActionConfigDialog";
import { getActionTypeIcon } from "./ActionTypeSelector";

import { Button } from "@/components/ui/button";

interface ActionItemProps {
	action: ModuleActionInfo;
	onEdit: () => void;
	onDelete: () => void;
	dragHandleProps?: Record<string, unknown>;
}

const COMPLETION_LABELS: Record<string, string> = {
	user_confirm: "Manual",
	task_webhook: "Automation",
	external_webhook: "Webhook",
	poll_condition: "Polling",
};

/**
 * Individual action item component with drag handle and actions.
 * @param props - component props
 * @param props.action - the action data to display
 * @param props.onEdit - callback when edit is clicked
 * @param props.onDelete - callback when delete is clicked
 * @param props.dragHandleProps - props for the drag handle element
 */
export function ActionItem({ action, onEdit, onDelete, dragHandleProps }: ActionItemProps) {
	const Icon = getActionTypeIcon(action.actionType?.type ?? "comment", action.actionType?.slug);

	return (
		<div className="group flex items-center gap-3 rounded-lg border bg-card p-3 hover:border-primary/50 transition-colors">
			{/* Drag Handle */}
			<div
				{...dragHandleProps}
				className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
			>
				<GripVertical className="h-5 w-5" />
			</div>

			{/* Icon */}
			<div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
				<Icon className="h-4 w-4" />
			</div>

			{/* Content */}
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2">
					<span className="font-medium truncate">{action.name}</span>
					{action.required && (
						<span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
							Required
						</span>
					)}
				</div>
				<div className="flex items-center gap-2 text-xs text-muted-foreground">
					<span>{action.actionType?.name ?? "Unknown type"}</span>
					<span>•</span>
					<span>{COMPLETION_LABELS[action.actionType?.completionType ?? "user_confirm"]}</span>
				</div>
			</div>

			{/* Actions */}
			<div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
				<Button variant="ghost" size="icon" onClick={onEdit}>
					<Pencil className="h-4 w-4" />
				</Button>
				<Button variant="ghost" size="icon" onClick={onDelete}>
					<Trash2 className="h-4 w-4 text-destructive" />
				</Button>
			</div>
		</div>
	);
}
